/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';
import { Link } from 'react-router';

import { compose } from 'recompose';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import mapProps from 'js/lib/mapProps';

import { Typography2 } from '@coursera/cds-core';

import StatusIcon, { getStatusIconLabel } from 'bundles/course-frame/components/course-outline/StatusIcon';
import withIsScheduleIncentivesEnabled from 'bundles/course-home/page-course-week/utils/withIsScheduleIncentivesEnabled';
import withSessionLabel from 'bundles/course-sessions/utils/withSessionLabel';
import type { SessionLabel } from 'bundles/course-sessions/utils/withSessionLabel';
import withSessionsV2EnrollmentEnabled from 'bundles/course-sessions/utils/withSessionsV2EnrollmentEnabled';
import { useIsFreemiumLocked } from 'bundles/enroll-common/utils/freemiumModelUtils';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';
import useCoachItemType from 'bundles/item-widget/hooks/useCoachItemType';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getFormattedLockMessage, getFormattedTitlePrefix, getIsGradable } from 'bundles/learner-progress/utils/Item';
import { canShowDistinctAvailabilityReviewDates } from 'bundles/learner-progress/utils/canShowDistinctAvailabilityReviewDates';
import { useCourseAiGradingContext } from 'bundles/peer/components/ai-grading/CourseAiEligibilityProvider';
import { truncToPlace } from 'bundles/primitive-formatting/utils/numberUtils';

import _t from 'i18n!nls/course-frame';

const styles = {
  container: css`
    display: flex;
    border-left: 4px solid transparent;
    background-color: var(--cds-color-neutral-background-primary);

    &:hover {
      text-decoration: none;
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }

    &:focus {
      text-decoration: none;
    }

    /* Ensure we can position the pseudo-element for the custom focus ring */
    position: relative;

    &:focus-visible {
      /* Remove the default outline so we can apply a custom one */
      outline: none;
    }

    /* Custom focus ring with rounded corners to match CDS styles */
    &:focus-visible::after {
      content: '';
      position: absolute;
      top: 0;
      left: -4px; /* compensate for the border-left in the container */
      right: 0;
      bottom: 0;
      pointer-events: none;
      border: 1px solid var(--cds-color-interactive-stroke-primary-focus);
      border-radius: var(--cds-border-radius-50);
    }
  `,
  freemiumLockedModule: css`
    background-color: var(--cds-color-emphasis-neutral-background-xweak);

    &:hover {
      background-color: var(--cds-color-emphasis-neutral-background-weak);
    }
  `,
  content: css`
    display: flex;
    width: 100%;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    gap: 12px;
  `,
  icon: css`
    align-self: center;
    height: 20px;
  `,
  selected: css`
    border-left: 4px solid var(--cds-color-interactive-primary);
    background-color: var(--cds-color-interactive-background-primary-hover-weak);

    &:hover {
      border-color: var(--cds-color-interactive-primary-hover);
    }
  `,
  freemiumSelected: css`
    background-color: var(--cds-color-emphasis-neutral-background-weak);
  `,
  highlightedTitle: css`
    font-weight: 600;
  `,
};

type PropsFromCaller = {
  computedItem: Item;
  highlighted?: boolean;
};

export type PropsFromCourseStore = {
  isGuidedProject: boolean;
  partnerIds: number[] | undefined;
};

type PropsFromHocs = {
  sessionsV2EnrollmentEnabled: boolean;
  sessionLabel: SessionLabel;
  isScheduleIncentivesEnabled: boolean;
};

export type PropsToComponent = PropsFromCaller & PropsFromHocs;

/**
 * This is Work-In-Progress component to replace the existing NavSingleItemDisplay.
 * @see https://github.com/webedx-spark/web/blob/main/static/bundles/learner-progress/components/item/nav/private/NavSingleItemDisplay.tsx
 */
export const NavSingleItemDisplayV2 = (props: PropsToComponent) => {
  const { computedItem, highlighted, sessionsV2EnrollmentEnabled, sessionLabel, isScheduleIncentivesEnabled } = props;
  const { getItemAiGradingEligibility, getItemFullyAiGradingEligibility } = useCourseAiGradingContext();
  const isAiGradingEligible =
    getItemAiGradingEligibility(computedItem.id) || getItemFullyAiGradingEligibility(computedItem.id);

  const coachItemType = useCoachItemType(computedItem);
  const { isGuidedProject, partnerIds } = useFluxibleStore(
    CourseStore,
    (courseStore): PropsFromCourseStore => ({
      isGuidedProject: courseStore.isGuidedProject(),
      partnerIds: courseStore.getMetadata()?.get('partnerIds'),
    })
  );

  const formattedTitlePrefix = getFormattedTitlePrefix(
    computedItem,
    isGuidedProject,
    isAiGradingEligible,
    coachItemType
  );

  const itemStatus = useMemo(() => {
    // Don't show any status info for the artificial "Review your peers" item type
    if (computedItem.contentSummary.typeName === 'splitPeerReviewItem') {
      return '';
    }

    if (!getIsGradable(computedItem)) {
      return '';
    }

    if (!computedItem.isSubmitted) {
      return '';
    }

    if (computedItem.isSubmitted && !computedItem.isGraded) {
      return _t('Results pending');
    }

    const grade = computedItem.itemGrade.get('overallOutcome').grade;
    const gradeFormatted = truncToPlace(grade * 100, 2);
    return _t('Grade: #{gradeFormatted}%', { gradeFormatted });
  }, [computedItem]);

  const iconLabelForLink = getStatusIconLabel(computedItem);

  const isFreemiumLocked = useIsFreemiumLocked({ moduleNumber: computedItem.weekNumber });

  const highlightedLabel = highlighted ? _t('selected link') : '';
  const ariaLabel = [highlightedLabel, formattedTitlePrefix, computedItem.name, iconLabelForLink, itemStatus]
    .filter((status) => !!status)
    .join(', ');

  // Course Frame: This is duplicated from static/bundles/learner-progress/components/item/locking/LockedTooltip.tsx
  const showDistinctAvailabilityReviewDates = canShowDistinctAvailabilityReviewDates(partnerIds);
  const { itemLockedStatus, itemLockedReasonCode, itemLockSummary, deadline } = computedItem;
  const lockMessage = getFormattedLockMessage({
    sessionsV2Enabled: sessionsV2EnrollmentEnabled,
    sessionLabel,
    itemLockedStatus,
    itemLockedReasonCode,
    itemLockSummary,
    isScheduleIncentivesEnabled,
    deadline,
    showDistinctAvailabilityReviewDates,
    isFreemiumLocked,
  });

  return (
    <Link
      to={computedItem.resourcePath}
      css={[
        styles.container,
        highlighted && styles.selected,
        isFreemiumLocked && styles.freemiumLockedModule,
        highlighted && isFreemiumLocked && styles.freemiumSelected,
      ]}
      aria-label={ariaLabel}
    >
      <div css={styles.content} className="outline-single-item-content-wrapper">
        <div css={styles.icon}>
          <StatusIcon computedItem={computedItem} tooltipMessage={lockMessage} />
        </div>
        <div>
          <Typography2
            variant="bodySecondary"
            component="div"
            color="body"
            css={highlighted && styles.highlightedTitle}
          >
            {computedItem.name}
          </Typography2>
          <Typography2 variant="bodySecondary" component="div" color="emphasis.neutral">
            {formattedTitlePrefix}
            {itemStatus ? ` • ${itemStatus}` : ''}
          </Typography2>
        </div>
      </div>
    </Link>
  );
};

// Course Frame: This HOC chain is duplicated from static/bundles/learner-progress/components/item/locking/LockedTooltip.tsx
export default compose<PropsToComponent, PropsFromCaller>(
  withSessionsV2EnrollmentEnabled((propsFromCaller) => propsFromCaller.computedItem.courseId),
  mapProps((propsFromCaller: PropsFromCaller) => ({
    courseId: propsFromCaller.computedItem.courseId,
  })),
  withSessionLabel,
  withIsScheduleIncentivesEnabled
)(NavSingleItemDisplayV2);
